import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Card, CardContent, Stack, TextField, Typography } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { getAuthMode } from '../lib/auth/authClient';
import type { AuthMode } from '../lib/auth/types';
import { errorMessage } from '../lib/api';

export function Login() {
  const { login, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState<AuthMode>('local');
  const [loginName, setLoginName] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void getAuthMode().then(setMode);
  }, []);

  const from = (location.state as { from?: string } | null)?.from || '/users';
  if (!isLoading && isAuthenticated) return <Navigate to={from} replace />;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await login(loginName.trim(), password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const isLocal = mode === 'local';

  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 2, bgcolor: 'background.default' }}>
      <Card sx={{ width: '100%', maxWidth: 400 }}>
        <CardContent sx={{ p: 4 }}>
          <Stack spacing={2} alignItems="center" sx={{ mb: 3 }}>
            <Box component="img" src="/semmco-logo.svg" alt="Semmco Life Protection Systems" sx={{ height: 64 }} />
            <Typography variant="h5">EEBD</Typography>
          </Stack>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
          >
            <Stack spacing={2}>
              {error && <Alert severity="error">{error}</Alert>}
              <TextField
                label={isLocal ? 'Username' : 'Email'}
                type={isLocal ? 'text' : 'email'}
                autoComplete="username"
                value={loginName}
                onChange={(e) => setLoginName(e.target.value)}
                autoFocus
                required
              />
              <TextField
                label="Password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <Button type="submit" variant="contained" size="large" disabled={busy}>
                {busy ? 'Signing in…' : 'Sign in'}
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}
